import { useState } from "react";

import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { PlusCircleIcon, PuzzlePieceIcon } from "@phosphor-icons/react/dist/ssr";
import { doc, updateDoc } from "firebase/firestore";

import {
  ProblemModal,
  RegenerateModal,
  TabHeader,
} from "@/components/Luna/features";
import CreateProblemModal from "@/components/Luna/features/CreateProblemModal";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { Problem, QuestionType } from "@/types";
import { db } from "@/utils/firebase";

import ProblemItem from "./ProblemItem";

import { ProblemSkeleton } from "./index";

const questionTypes = [
  QuestionType.MultipleChoice,
  QuestionType.TrueFalse,
  QuestionType.ShortAnswer,
];

const Problems = () => {
  const { lessonInteractionProfile, lesson } = useLesson();
  const [isGenerating, setIsGenerating] = useState(false);
  // const [isCreatingManualProblem, setIsCreatingManualProblem] = useState(false);
  const [selectedProblemIndex, setSelectedProblemIndex] = useState<
    number | null
  >(null);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(
    null
  );
  const { aiAxios } = useFirebaseContext();
  const problems = lessonInteractionProfile?.realtimeResources?.problems || [];
  const sortedProblems = [...problems].reverse();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [isCreateProblemModalOpen, setIsCreateProblemModalOpen] =
    useState(false);
  const onOpenModal = (index: number) => {
    setSelectedProblemIndex(index);
    setIsModalOpen(true);
  };

  const handleGenerateProblem = async (
    question?: string,
    difficulty?: string,
    questionType?: QuestionType
  ) => {
    let nextType = questionType;
    if (!questionType) {
      questionType = questionTypes[0];

      // Rotate through question types to ensure variety
      const lastProblemType = problems[problems.length - 1]?.type;
      const currentIndex = lastProblemType
        ? questionTypes.indexOf(lastProblemType)
        : -1;
      const nextIndex = (currentIndex + 1) % questionTypes.length;
      nextType = questionTypes[nextIndex];
    }

    let nextDifficulty = difficulty;
    if (!difficulty) {
      // Smart difficulty progression based on performance
      const completedProblems = problems.filter((p) => p.studentAnswer);
      const avgScore =
        completedProblems.length > 0
          ? completedProblems.reduce((sum, p) => sum + (p.score || 0), 0) /
          completedProblems.length
          : 0.5;

      nextDifficulty =
        avgScore > 0.8 ? "hard" : avgScore > 0.6 ? "medium" : "easy";
    }

    setIsGenerating(true);
    aiAxios
      .post("luna/problems", {
        lessonId: lesson?.id,
        difficulty: nextDifficulty,
        questionType: nextType,
        questionPrompt: question,
      })
      .catch((error) => {
        console.error("Error generating problem:", error);
        // TODO: Show user-friendly error message
        alert(
          error.response?.data?.error ||
          "Failed to generate problem. Please try again."
        );
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const handleApproveProblem = async (index: number) => {
    const problemToApprove =
      lessonInteractionProfile?.realtimeResources?.problems[index];
    if (!problemToApprove) return;

    try {
      const lessonRef = doc(
        db,
        "lessonInteractionProfiles",
        lessonInteractionProfile.id
      );
      await updateDoc(lessonRef, {
        realtimeResources: {
          ...lessonInteractionProfile.realtimeResources,
          problems: (
            lessonInteractionProfile.realtimeResources?.problems || []
          ).map((problem, i) =>
            i === index ? {
              ...problem,
              isApproved: true
            } : problem
          ),
        },
      });
    } catch (error) {
      console.error("Error approving problem:", error);
    }
  };

  const handleRegenerateProblem = (prompt: string) => {
    if (regeneratingIndex === null || !lessonInteractionProfile) return;
    const problemToRegenerate =
      lessonInteractionProfile?.realtimeResources?.problems[regeneratingIndex];
    aiAxios
      .put("luna/problems", {
        lessonId: lesson?.id,
        problemId: problemToRegenerate.id,
        prompt: prompt,
        questionType: problemToRegenerate.type,
      })
      .finally(() => {
        setIsRegenerateModalOpen(false);
        setRegeneratingIndex(null);
      });
  };

  const handleOpenRegenerateModal = (index: number) => {
    setRegeneratingIndex(index);
    setIsRegenerateModalOpen(true);
  };

  const handleDeleteProblem = async (index: number) => {
    if (confirm("Are you sure you want to delete this problem?")) {
      const problemToDelete =
        lessonInteractionProfile?.realtimeResources?.problems[index];
      if (!problemToDelete) return;

      try {
        const lessonRef = doc(
          db,
          "lessonInteractionProfiles",
          lessonInteractionProfile.id
        );
        await updateDoc(lessonRef, {
          realtimeResources: {
            ...lessonInteractionProfile.realtimeResources,
            problems: (
              lessonInteractionProfile.realtimeResources?.problems || []
            ).filter((_, i) => i !== index),
          },
        });
      } catch (error) {
        console.error("Error deleting problem:", error);
      }
    }
  };

  const handleUpdateProblem = async (
    index: number,
    updatedProblem: Problem
  ) => {
    if (!lessonInteractionProfile) return;
    const lessonRef = doc(
      db,
      "lessonInteractionProfiles",
      lessonInteractionProfile.id
    );
    await updateDoc(lessonRef, {
      realtimeResources: {
        ...lessonInteractionProfile.realtimeResources,
        problems: (
          lessonInteractionProfile.realtimeResources?.problems || []
        ).map((problem, i) => (i === index ? updatedProblem : problem)),
      },
    });
    setIsEditing(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setSelectedProblemIndex(null);
  };

  return (
    <Box
      width="100%"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <TabHeader
        title="Problems"
        icon={<PuzzlePieceIcon size={16} weight="fill" />}
        handleGenerate={() => {
          handleGenerateProblem();
        }}
        isGenerating={isGenerating}
        actions={
          <Tooltip title="Create Manually" arrow>
            <IconButton
              disabled={isGenerating}
              onClick={() => !isGenerating && setIsCreateProblemModalOpen(true)}
              sx={{
                p: .75,
                border: "1px solid",
                borderColor: "secondary.main",
                borderRadius: 1.5,
                color: "text.primary",
                "&.Mui-disabled": {
                  color: "text.disabled",
                  borderColor: "divider",
                },
              }}
            >
              <PlusCircleIcon size={16} weight="fill" />
            </IconButton>
          </Tooltip>
        }
      />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0, // Ensures the Box can shrink properly in a flex column
        }}
      >
        {isGenerating && <ProblemSkeleton />}
        {problems.length < 1 && !isGenerating ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 4,
            }}
          >
            <Typography
              variant="text15B"
              color="textSecondary"
              textAlign="center"
              sx={{ width: "100%" }}
            >
              Assess learning with problems
            </Typography>
            <Typography
              variant="text13L"
              color="textSecondary"
              textAlign="center"
              sx={{
                width: "100%",
                opacity: 0.8
              }}
            >
              Work with Luna to draft targeted practice problems and check your student's understanding of the lesson.
            </Typography>
          </Box>
        ) : (
          <>
            {sortedProblems.map((problem) => {
              const originalIndex = problems.findIndex(
                (p) => p.id === problem.id
              );

              return regeneratingIndex === originalIndex ? (
                <ProblemSkeleton key={problem.id} />
              ) : (
                <ProblemItem
                  key={problem.id}
                  problem={problem}
                  index={originalIndex}
                  onApprove={handleApproveProblem}
                  onRegenerate={handleOpenRegenerateModal}
                  onDelete={handleDeleteProblem}
                  onOpenModal={onOpenModal}
                  onEdit={() => {
                    setIsModalOpen(true);
                    setSelectedProblemIndex(originalIndex);
                    setIsEditing(true);
                  }}
                />
              );
            })}
          </>
        )}
      </Box>

      {selectedProblemIndex !== null && (
        <ProblemModal
          problem={problems[selectedProblemIndex]}
          index={selectedProblemIndex}
          open={isModalOpen}
          onClose={handleCloseModal}
          canSubmit={false}
          onApprove={() => {
            handleApproveProblem(selectedProblemIndex);
            handleCloseModal();
          }}
          onEdit={() => {
            setIsModalOpen(true);
            setIsEditing(true);
            setSelectedProblemIndex(selectedProblemIndex);
          }}
          onRegenerate={() => {
            handleOpenRegenerateModal(selectedProblemIndex);
            handleCloseModal();
          }}
          onDelete={() => {
            handleDeleteProblem(selectedProblemIndex);
            handleCloseModal();
          }}
          onUpdate={handleUpdateProblem}
          onCancelEdit={() => {
            setIsEditing(false);
          }}
          isEditing={isEditing}
        />
      )}
      {regeneratingIndex !== null && (
        <RegenerateModal
          isOpen={isRegenerateModalOpen}
          onClose={() => {
            setIsRegenerateModalOpen(false);
            setRegeneratingIndex(null);
          }}
          onRegenerate={(prompt) => {
            setIsRegenerateModalOpen(false);
            handleRegenerateProblem(prompt);
          }}
        />
      )}
      <CreateProblemModal
        isOpen={isCreateProblemModalOpen}
        onClose={() => setIsCreateProblemModalOpen(false)}
        onGenerate={handleGenerateProblem}
      />
    </Box>
  );
};

export default Problems;
